import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AppService } from 'src/app/app.service';
import { DynamicLinkService } from './dynamic-link.service';
import { LocalStorageService } from './localstorage.service';

export interface HtmlCtaClickOptions {
  preserveWalletLoginRedirect?: boolean;
}

export interface HtmlCtaTransformOptions {
  preserveDocument?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class HtmlCtaService {
  constructor(
    private appService: AppService,
    private router: Router,
    private localStorageService: LocalStorageService,
    private dynamicLinkService: DynamicLinkService,
  ) {}

  handleClick(event: Event, options: HtmlCtaClickOptions = {}) {
    const target = event.target as HTMLElement | null;
    const ctaButton = target?.closest?.('.cta-button') as HTMLElement | null;

    if (!ctaButton) return false;

    event.preventDefault();
    this.navigate(this.getCtaLink(ctaButton), options);
    return true;
  }

  transformCtaButtons(html: string, options: HtmlCtaTransformOptions = {}) {
    if (!html) return '';

    const document = new DOMParser().parseFromString(html, 'text/html');

    document.querySelectorAll<HTMLElement>('.cta-button').forEach(ctaButton => {
      const text = this.getCtaText(ctaButton);
      if (text) this.setCtaLabel(ctaButton, text);
    });

    if (options.preserveDocument && /<(?:html|head|body)\b/i.test(html)) {
      return document.documentElement.outerHTML;
    }

    return document.body.innerHTML;
  }

  navigate(rawLink: string, options: HtmlCtaClickOptions = {}) {
    const link = this.resolveDynamicLink((rawLink || '').trim());
    if (!link) return;

    if (/^https?:\/\//i.test(link)) {
      window.open(link, '_blank', 'noopener');
      return;
    }

    if (this.isBlockedLink(link)) return;

    if (link.startsWith('?')) {
      const currentPath = this.router.url.split('?')[0] || `/${this.appService.languageCode}`;
      const targetUrl = `${currentPath}${link}`;
      const dialog = new URLSearchParams(link.slice(1)).get('dialog')?.toLowerCase();

      if (options.preserveWalletLoginRedirect && !this.isLoggedIn && dialog === 'wallet') {
        this.localStorageService.pendingLoginRedirectUrl = targetUrl;
        this.appService.jumpToLogin();
        return;
      }

      this.router.navigateByUrl(targetUrl);
      return;
    }

    const splitIndex = link.search(/[?#]/);
    const pathPart = splitIndex === -1 ? link : link.slice(0, splitIndex);
    const suffix = splitIndex === -1 ? '' : link.slice(splitIndex);
    const normalizedPath = pathPart.startsWith('/') ? pathPart : `/${pathPart}`;
    const languageCode = this.appService.languageCode;
    const firstSegment = normalizedPath.split('/')[1] || '';
    const finalPath =
      languageCode && firstSegment !== languageCode ? `/${languageCode}${normalizedPath}` : normalizedPath;

    this.router.navigateByUrl(`${finalPath}${suffix}`);
  }

  private getCtaLink(ctaButton: HTMLElement) {
    if (this.isLoggedIn) {
      return ctaButton.getAttribute('data-link') || ctaButton.getAttribute('href') || '';
    }

    return (
      ctaButton.getAttribute('data-logged-out-link') ||
      ctaButton.getAttribute('data-link') ||
      ctaButton.getAttribute('href') ||
      ''
    );
  }

  private getCtaText(ctaButton: HTMLElement) {
    if (this.isLoggedIn) {
      return ctaButton.getAttribute('data-text') || '';
    }

    return ctaButton.getAttribute('data-logged-out-text') || ctaButton.getAttribute('data-text') || '';
  }

  private setCtaLabel(ctaButton: HTMLElement, text: string) {
    const label =
      ctaButton.querySelector<HTMLElement>('.cta-label, label') || ctaButton.ownerDocument.createElement('label');

    label.classList.add('cta-label', 'label');
    label.textContent = text;

    if (!label.parentElement) {
      ctaButton.textContent = '';
      ctaButton.appendChild(label);
    }
  }

  private get isLoggedIn() {
    return Boolean(this.localStorageService.loginToken);
  }

  /**
   * ${domain} 动态链接 (ALL-13956)
   *
   * 后台允许 URL 与富文本链接用 ${domain} 开头：同网域转成站内路径（含语系），
   * 其他网域换成完整网址，交给下方原本的流程处理。没有 ${domain} 的链接原样返回。
   */
  private resolveDynamicLink(link: string) {
    if (!this.dynamicLinkService.hasDomainPlaceholder(link)) return link;
    return this.dynamicLinkService.getInternalPath(link) ?? this.dynamicLinkService.replaceDomainPlaceholder(link);
  }

  private isBlockedLink(link: string) {
    const normalizedLink = link.replace(/[\u0000-\u001F\u007F\s]+/g, '').toLowerCase();
    const hasUnsupportedProtocol = /^[a-z][a-z0-9+.-]*:/i.test(normalizedLink) && !/^https?:\/\//i.test(normalizedLink);

    return /^\/\//.test(normalizedLink) || hasUnsupportedProtocol;
  }
}
